import { useState } from 'react'
import { initiateOAuth } from '../../utils/api'

export default function SignupModal({ onClose }) {
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [loadingProvider, setLoadingProvider] = useState(null)

  const handleOAuthClick = async (provider) => {
    try {
      setLoadingProvider(provider)
      initiateOAuth(provider)
    } catch (error) {
      console.error(`OAuth error for ${provider}:`, error)
      setLoadingProvider(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 animate-fade-in">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Join ContentIndexer</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Free Credit Banner */}
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900 border-2 border-green-300 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-gift text-green-500 text-2xl mr-3"></i>
            <div className="text-sm text-green-900 dark:text-green-100">
              <strong>Free Every Month:</strong> $5 in credits to explore all extractors!
            </div>
          </div>
        </div>

        {/* OAuth Buttons */}
        <div className="space-y-3 mb-6">
          <button
            onClick={() => handleOAuthClick('github')}
            disabled={loadingProvider === 'github'}
            className="w-full flex items-center justify-center py-4 px-6 rounded-lg shadow-md bg-[#24292e] text-white text-lg font-semibold hover:bg-[#1a1e22] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
            {loadingProvider === 'github' ? 'Redirecting...' : 'Continue with GitHub'}
          </button>
          <button
            onClick={() => handleOAuthClick('google')}
            disabled={loadingProvider === 'google'}
            className="w-full flex items-center justify-center py-4 px-6 rounded-lg shadow-md bg-white text-gray-700 text-lg font-semibold hover:bg-gray-50 border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {loadingProvider === 'google' ? 'Redirecting...' : 'Continue with Google'}
          </button>
        </div>

        {/* Email Toggle */}
        <div className="text-center mb-4">
          <button
            onClick={() => setShowEmailForm(!showEmailForm)}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 font-medium"
          >
            {showEmailForm ? 'Hide email signup' : 'Or sign up with email'}
          </button>
        </div>

        {/* Email Form */}
        {showEmailForm && (
          <div className="space-y-4 animate-fade-in border-t border-gray-300 dark:border-gray-700 pt-4">
            <input type="email" placeholder="Email" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
            <input type="text" placeholder="Username" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
            <input type="password" placeholder="Password" minLength="8" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
            <input type="password" placeholder="Confirm Password" minLength="8" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
            <button className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700">
              Create Account
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
